import { useState, FormEvent, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../app/AuthContext";
import { UUID } from "crypto";
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  TextField,
  CircularProgress,
  Alert,
} from "@mui/material";
import { VpnKey } from "@mui/icons-material";
import { useRecordingOpportunity } from "../../../app/RecordingOpportunityContext";

export function CodeInputPage() {
  const [searchParams] = useSearchParams();
  const [codeInput, setCodeInput] = useState("");
  const [error, setError] = useState("");
  const [attemptedFromUrl, setAttemptedFromUrl] = useState(false);
  const navigate = useNavigate();
  const { isLoading, isAuthenticated, setUuid } = useAuth();
  const { uploadSuccess } = useRecordingOpportunity();

  // Auto-submit if code is in URL
  useEffect(() => {
    const codeFromUrl = searchParams.get("code");
    if (codeFromUrl && !attemptedFromUrl) {
      setAttemptedFromUrl(true);
      setUuid(codeFromUrl as UUID);
    }
  }, [searchParams, setUuid, attemptedFromUrl]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/record");
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    const trimmedCode = codeInput.trim();
    if (trimmedCode) {
      setUuid(trimmedCode as UUID);
    }
  };

  // Show error if auth failed from URL code
  const codeFromUrl = searchParams.get("code");
  if (attemptedFromUrl && !isLoading && !isAuthenticated && codeFromUrl) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: 4 }}>
        <Container maxWidth="md">
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Alert severity="error" sx={{ mb: 3 }}>
              This recording link is invalid or has already been used.
            </Alert>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/", { replace: true })}
            >
              Go back
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Show loading state when verifying from URL
  if (isLoading && codeFromUrl) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: 4 }}>
        <Container maxWidth="md">
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress sx={{ mb: 2 }} color="primary" />
            <Typography variant="h6" color="text.primary">
              Verifying your access link...
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: 4 }}>
      <Container maxWidth="md">
        <Paper sx={{ p: 4 }}>
          {uploadSuccess && (
            <Alert severity="success" sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Thank you! 🎉
              </Typography>
              <Typography variant="body1">
                Your voice recording has been successfully uploaded!
              </Typography>
            </Alert>
          )}

          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            color="primary"
            sx={{ textAlign: "center" }}
          >
            Record a Voice Message
          </Typography>

          <Typography
            variant="body1"
            color="text.primary"
            sx={{ mb: 4, textAlign: "center" }}
          >
            Enter your access code to record a voice message that will be played
            during the race.
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ maxWidth: 400, mx: "auto" }}
          >
            <TextField
              fullWidth
              id="code"
              label="Access Code"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value)}
              placeholder="Enter your access code"
              required
              variant="outlined"
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <VpnKey sx={{ mr: 1, color: "text.secondary" }} />
                ),
              }}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              color="primary"
              disabled={isLoading || !codeInput.trim()}
              startIcon={
                isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <VpnKey />
                )
              }
            >
              {isLoading ? "Verifying..." : "Continue"}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
